import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'

import { addDays, endOfWeek, format } from 'date-fns'
import { HiCalendar } from 'react-icons/hi2'

import type { DateWithMessage } from '@/components/common/Calendar'
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxList,
  ComboboxSelectableItem,
  ComboboxTrigger,
} from '@/components/common/ComboboxV2'
import { DayPickerDialog as DayPickerDialogBase } from '@/components/common/Daypicker'
import { ActionButton } from '@/components/tasks/v2/common/ActionButton'

import { useActivePursuitId } from '@/hooks/pursuits/useActivePursuitId'
import { usePursuit } from '@/hooks/pursuits/usePursuit'

import type { Alignment, Side } from '@/types/components/Radix'

export interface DateOption {
  label: string
  value: Date
}

interface DateOptionWithMatch extends DateOption {
  match: (date: Date) => boolean
}

interface DialogConfig {
  allowNull?: boolean
  dialogDatesWithMessages?: DateWithMessage[]
}

export interface DateComboboxProps {
  open: boolean
  align?: Alignment
  side?: Side
  triggerAs?: React.ElementType
  triggerClassName?: string
  children?: React.ReactNode
  portal?: boolean
  performant?: boolean
  value: Date | null
  customDates: DateOption[]
  dialogConfig: DialogConfig
  onChange: (value: Date | null) => void
  setOpen: (open: boolean) => void
  disabled?: boolean
}

const hashDate = (date: Date | undefined | null, floor: boolean = true) => {
  if (!date) {
    return null
  }
  if (floor) {
    // create a new date object to avoid mutating the original
    const newDate = new Date(date)
    newDate.setHours(0, 0, 0, 0)
    return newDate.toISOString()
  }
  return date.toISOString()
}

export const useActivePursuitDates = () => {
  const { activePursuitId } = useActivePursuitId()
  const { data: pursuit } = usePursuit({ pursuitId: activePursuitId })

  const customDates = useMemo(() => {
    if (pursuit?.due_date) {
      return [
        {
          label: 'Pursuit due date',
          value: pursuit.due_date,
        },
      ]
    }
    return []
  }, [pursuit?.due_date])

  const dialogDatesWithMessages = useMemo(() => {
    if (pursuit?.due_date) {
      return [
        {
          message: 'Pursuit due date',
          date: pursuit.due_date,
        },
      ]
    }
    return []
  }, [pursuit?.due_date])

  return { customDates, dialogDatesWithMessages }
}

export const DateCombobox = forwardRef<HTMLDivElement, DateComboboxProps>(
  (
    {
      open,
      side = 'bottom',
      align = 'start',
      triggerAs: TriggerAs,
      triggerClassName,
      children,
      portal,
      performant,
      value,
      customDates,
      dialogConfig,
      onChange,
      setOpen,
      disabled = false,
    },
    ref,
  ) => {
    const [dialogOpen, setDialogOpen] = useState(false)
    const [triggerWidth, setTriggerWidth] = useState<number>(0)

    const triggerRef = useRef<HTMLDivElement>(null)

    useImperativeHandle(ref, () => triggerRef.current!)

    const isEndOfWeek = useCallback(
      (date: Date) =>
        date.getDay() === endOfWeek(new Date()).getDay() &&
        date.getHours() === 23 &&
        date.getMinutes() === 59 &&
        date.getSeconds() === 59,
      [],
    )

    const options: DateOptionWithMatch[] = useMemo(() => {
      let options = [
        {
          label: 'Tomorrow',
          value: addDays(new Date(), 1),
          match: (date: Date) =>
            hashDate(date) === hashDate(addDays(new Date(), 1)) &&
            !isEndOfWeek(date),
        },
        {
          label: 'End of this week',
          value: endOfWeek(new Date()),
          match: isEndOfWeek,
        },
        {
          label: 'In one week',
          value: addDays(new Date(), 7),
          match: (date: Date) =>
            hashDate(date) === hashDate(addDays(new Date(), 7)) &&
            !isEndOfWeek(date),
        },
      ]
      for (const customDate of customDates) {
        options.push({
          label: customDate.label,
          value: customDate.value,
          match: (date: Date) =>
            hashDate(date) === hashDate(customDate.value) && !isEndOfWeek(date),
        })
      }
      return options
    }, [customDates, isEndOfWeek])

    const isCustomSelected = useMemo(() => {
      return value === null
        ? false
        : !options.some((option) => option.match(value))
    }, [value])

    const handleSelect = (value: string) => {
      // search by label
      const option = options.find((option) => option.label === value)
      if (option) {
        onChange(option.value)
      }
      setOpen(false)
    }

    const handleCustomSelect = (date: Date | null) => {
      onChange(date)
      setDialogOpen(false)
    }

    const onOpenChange = (open: boolean) => {
      // for some reason, the combobox closing when the user click on custom
      // causes the dialog to "double" open, so delaying the setOpen fixes it
      setTimeout(() => {
        setOpen(open)
      })
    }

    const buttonChildren = (
      <>
        <HiCalendar className="flex-shrink-0 h-4 w-4" />
        <span
          className={
            value ? undefined : 'text-primary-500 dark:text-primary-400'
          }
        >
          {value ? format(value, 'LLL d, y h:mm a') : 'Set due date'}
        </span>
      </>
    )

    const trigger = (
      <div ref={triggerRef} className="flex justify-end">
        {children !== undefined ? (
          children
        ) : TriggerAs ? (
          <TriggerAs className={triggerClassName}>{buttonChildren}</TriggerAs>
        ) : (
          <ActionButton className={triggerClassName}>
            {buttonChildren}
          </ActionButton>
        )}
      </div>
    )

    useEffect(() => {
      if (triggerRef.current) {
        setTriggerWidth(triggerRef.current.offsetWidth)
      }
    }, [value, triggerRef.current?.offsetWidth])

    return (
      <>
        {dialogOpen && (
          <DayPickerDialogBase
            open={dialogOpen}
            initialValue={value}
            allowNull={dialogConfig.allowNull}
            datesWithMessages={dialogConfig.dialogDatesWithMessages}
            onChange={handleCustomSelect}
            setOpen={setDialogOpen}
          />
        )}
        {/* set minimum width to make sure that switching between performant and non-performant doesn't cause a layout shift */}
        <div style={{ minWidth: triggerWidth }}>
          {performant && !open && trigger}
          {
            // if performant, only show the combobox when it's open
            (performant ? open : true) && (
              <Combobox open={open} onOpenChange={onOpenChange}>
                <ComboboxTrigger
                  asChild={TriggerAs || children ? false : true}
                  className="flex items-center w-full"
                  disabled={disabled}
                >
                  {children !== undefined ? (
                    children
                  ) : TriggerAs ? (
                    <TriggerAs className={triggerClassName}>
                      {buttonChildren}
                    </TriggerAs>
                  ) : (
                    <ActionButton className={triggerClassName}>
                      {buttonChildren}
                    </ActionButton>
                  )}
                </ComboboxTrigger>
                <ComboboxContent
                  className="p-0 sm:max-w-48"
                  side={side}
                  align={align}
                  portal={portal}
                >
                  <ComboboxInput
                    placeholder="Set due date..."
                    className="placeholder:text-[13px]"
                  />
                  <ComboboxList>
                    <ComboboxEmpty className="text-[13px]">
                      No results found.
                    </ComboboxEmpty>
                    <ComboboxGroup>
                      {options.map((option, idx) => {
                        const isSelected =
                          value === null ? false : option.match(value)
                        return (
                          <ComboboxSelectableItem
                            key={idx}
                            value={option.label}
                            onSelect={handleSelect}
                            isSelected={isSelected}
                          >
                            <div className="flex items-center gap-x-2">
                              <HiCalendar className="flex-shrink-0 h-[14px] w-[14px]" />
                              <span>{option.label}</span>
                            </div>
                          </ComboboxSelectableItem>
                        )
                      })}
                      <ComboboxSelectableItem
                        value="custom"
                        onSelect={() => setDialogOpen(true)}
                        isSelected={isCustomSelected}
                      >
                        <div className="flex items-center justify-between w-full gap-x-2 text-[13px]">
                          <div className="flex items-center gap-x-2">
                            <HiCalendar className="flex-shrink-0 h-[14px] w-[14px]" />
                            <span>Custom</span>
                          </div>
                        </div>
                      </ComboboxSelectableItem>
                    </ComboboxGroup>
                  </ComboboxList>
                </ComboboxContent>
              </Combobox>
            )
          }
        </div>
      </>
    )
  },
)

DateCombobox.displayName = 'ComboboxV2.DateCombobox'
